// /app/audit — workspace publish-attempt audit timeline.
//
// Backend: GET /v1/audit?days=&brand=&platform=&outcome=&limit=
//          (content-service v0.2.71+)
//
// Ported visually from design-handoff/src/pages/secondary.jsx AuditPage:
// a card-wrapped grid table with hover row tint + per-source coloured
// pill. Inline styles + tokens.css utility classes only.

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useBrands } from "@/hooks/use-persona";
import { usePostDrawer } from "@/components/PostDrawer";
import { PageHead, BrandMark } from "@/components/primitives";
import { Icons } from "@/components/icons";
import type { Brand, Platform } from "@/lib/types";

interface AuditEntry {
  id: string;
  post_id: string;
  brand_id: string;
  platform: Platform;
  attempt_number: number;
  started_at: string;
  finished_at?: string;
  outcome: string;
  platform_status?: number;
  platform_post_id?: string;
  platform_permalink?: string;
  error_reason?: string;
  image_attached: boolean;
  text_snippet: string;
}

interface AuditResponse {
  days: number;
  from: string;
  to: string;
  total: number;
  entries: AuditEntry[];
}

export const Route = createFileRoute("/app/audit")({
  component: AuditPage,
});

const PLATFORM_LABEL: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X",
  linkedin: "LinkedIn",
  telegram: "Telegram",
};

const OUTCOME_TONE_VAR: Record<string, string> = {
  success: "var(--success)",
  pending: "var(--warning)",
  platform_error: "var(--error)",
  no_token: "var(--error)",
  timeout: "var(--error)",
  internal_error: "var(--error)",
  cancelled: "var(--mu)",
};

function AuditPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";

  const [days, setDays] = useState<1 | 7 | 30>(7);
  const [outcome, setOutcome] = useState<string>("all");
  const [platform, setPlatform] = useState<Platform | "all">("all");
  const [brandFilter, setBrandFilter] = useState<string | "all">("all");

  const { data: brandsData } = useBrands();
  const brands = brandsData?.data ?? [];
  const brandMap = new Map<string, Brand>(brands.map((b) => [b.id, b]));

  const params = new URLSearchParams({ days: String(days), limit: "200" });
  if (outcome !== "all") params.set("outcome", outcome);
  if (platform !== "all") params.set("platform", platform);
  if (brandFilter !== "all") params.set("brand", brandFilter);

  const { data, isLoading } = useQuery({
    queryKey: ["audit", days, outcome, platform, brandFilter],
    queryFn: () => api<AuditResponse>(`/audit?${params.toString()}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const entries = data?.entries ?? [];

  const OUTCOMES = [
    { id: "all", label: tr ? "Tümü" : "All" },
    { id: "success", label: tr ? "Başarılı" : "Success" },
    // "failure" is a SYNTHETIC bucket in content-service's audit store
    // (outcome NOT IN ('success','pending')); every other value is matched
    // with plain equality. Filtering on "platform_error" therefore hid the
    // no_token / timeout / internal_error / cancelled rows this same file
    // knows how to label.
    { id: "failure", label: tr ? "Hata" : "Error" },
    { id: "pending", label: tr ? "Devam" : "Pending" },
  ];
  const OUTCOME_LABEL: Record<string, string> = tr
    ? {
        success: "başarılı",
        pending: "devam",
        platform_error: "platform hatası",
        no_token: "token yok",
        timeout: "zaman aşımı",
        internal_error: "iç hata",
        cancelled: "iptal",
      }
    : {
        success: "success",
        pending: "pending",
        platform_error: "platform error",
        no_token: "no token",
        timeout: "timeout",
        internal_error: "internal error",
        cancelled: "cancelled",
      };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "10 · Etkinlik kaydı" : "10 · Audit log"}
        title={tr ? "Olan biten her şey." : "Everything that happened."}
        subtitle={
          tr
            ? "Tüm markalardaki platform yayın denemeleri. Yalnızca okunur. Aktör, marka, kaynağa göre filtrele."
            : "Every platform publish attempt across the workspace. Read-only. Filter by actor, brand, source."
        }
      />

      <div className="scroll-y" style={{ flex: 1, padding: "24px 32px 48px" }}>
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <div style={{ display: "inline-flex", gap: 4 }}>
            {([1, 7, 30] as const).map((n) => {
              const on = n === days;
              return (
                <button
                  key={n}
                  onClick={() => setDays(n)}
                  className={on ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
                >
                  {n === 1 ? (tr ? "24 sa" : "24h") : tr ? `${n} gün` : `${n}d`}
                </button>
              );
            })}
          </div>

          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value as string)}
            className="input"
            style={{ width: "auto", padding: "6px 10px", fontSize: 12 }}
          >
            <option value="all">{tr ? "Tüm markalar" : "All brands"}</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.trade_name}
              </option>
            ))}
          </select>

          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform | "all")}
            className="input"
            style={{ width: "auto", padding: "6px 10px", fontSize: 12 }}
          >
            <option value="all">{tr ? "Tüm platformlar" : "All platforms"}</option>
            {(["instagram", "facebook", "x", "linkedin", "telegram"] as Platform[]).map((p) => (
              <option key={p} value={p}>
                {PLATFORM_LABEL[p]}
              </option>
            ))}
          </select>

          <div style={{ display: "inline-flex", gap: 4 }}>
            {OUTCOMES.map((o) => {
              const on = o.id === outcome;
              return (
                <button
                  key={o.id}
                  onClick={() => setOutcome(o.id)}
                  className={on ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}
                >
                  {o.label}
                </button>
              );
            })}
          </div>

          <span
            className="mono"
            style={{ marginLeft: "auto", fontSize: 11, color: "var(--mu)" }}
          >
            {data?.total ?? 0} {tr ? "kayıt" : "entries"}
          </span>
        </div>

        {/* Table card */}
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "90px 130px 1fr 1fr 140px 60px",
              borderBottom: "1px solid var(--b1)",
              background: "var(--base)",
            }}
          >
            {[
              tr ? "Saat" : "Time",
              tr ? "Sonuç" : "Outcome",
              tr ? "Marka" : "Brand",
              tr ? "Değişiklik" : "Change",
              tr ? "Platform" : "Platform",
              "",
            ].map((label, i) => (
              <div
                key={i}
                className="mono"
                style={{
                  padding: "10px 14px",
                  fontSize: 10,
                  color: "var(--mu)",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                {label}
              </div>
            ))}
          </div>

          {isLoading ? (
            <RowSkeleton />
          ) : entries.length === 0 ? (
            <div
              style={{
                padding: 48,
                textAlign: "center",
                color: "var(--mu)",
                fontSize: 13,
              }}
            >
              {tr ? "Bu aralıkta kayıt yok." : "No entries in this range."}
            </div>
          ) : (
            entries.map((e) => (
              <Row
                key={e.id}
                entry={e}
                brand={brandMap.get(e.brand_id)}
                outcomeLabel={OUTCOME_LABEL[e.outcome] ?? e.outcome}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  entry,
  brand,
  outcomeLabel,
}: {
  entry: AuditEntry;
  brand: Brand | undefined;
  outcomeLabel: string;
}) {
  const open = usePostDrawer((s) => s.open);
  const tone = OUTCOME_TONE_VAR[entry.outcome] ?? "var(--mu)";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => open(entry.post_id)}
      style={{
        display: "grid",
        gridTemplateColumns: "90px 130px 1fr 1fr 140px 60px",
        alignItems: "center",
        borderBottom: "1px solid var(--b1)",
        fontSize: 12.5,
        transition: "background 150ms",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--elev)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div className="mono" style={{ padding: "12px 14px", fontSize: 11, color: "var(--mu)" }}>
        {formatTime(entry.started_at)}
      </div>
      <div style={{ padding: "12px 14px" }}>
        <span
          className="mono"
          style={{
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 3,
            background: `${cssVarToRgba(tone, 0.13)}`,
            color: tone,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {outcomeLabel}
        </span>
      </div>
      <div
        style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 6 }}
      >
        {brand ? <BrandMark brand={brand} size={16} /> : null}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {brand?.trade_name ?? entry.brand_id.slice(0, 8)}
        </span>
      </div>
      <div
        className="mono"
        style={{
          padding: "12px 14px",
          fontSize: 11,
          color: "var(--tx)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {entry.error_reason || entry.text_snippet || "—"}
      </div>
      <div className="mono" style={{ padding: "12px 14px", fontSize: 11, color: "var(--tx2)" }}>
        {PLATFORM_LABEL[entry.platform]} #{entry.attempt_number}
      </div>
      <div style={{ padding: "12px 14px" }}>
        <button
          className="btn-icon"
          onClick={(e) => {
            e.stopPropagation();
            open(entry.post_id);
          }}
        >
          <Icons.More size={14} />
        </button>
      </div>
    </div>
  );
}

function RowSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "90px 130px 1fr 1fr 140px 60px",
            alignItems: "center",
            borderBottom: "1px solid var(--b1)",
            padding: "12px 14px",
            gap: 10,
          }}
        >
          {Array.from({ length: 6 }).map((__, j) => (
            <div key={j} className="skel" style={{ height: 12 }} />
          ))}
        </div>
      ))}
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "?";
  }
}

// Translate a css `var(--*)` to a faint rgba(…) for the pill background.
// We only support the 4 token vars we use here — anything else falls back.
function cssVarToRgba(cssVar: string, alpha: number): string {
  const m: Record<string, string> = {
    "var(--success)": `rgba(34,197,94,${alpha})`,
    "var(--warning)": `rgba(245,158,11,${alpha})`,
    "var(--error)": `rgba(239,68,68,${alpha})`,
    "var(--info)": `rgba(59,130,246,${alpha})`,
    "var(--mu)": `rgba(96,96,96,${alpha})`,
    "var(--accent)": `rgba(224,45,28,${alpha})`,
  };
  return m[cssVar] ?? `rgba(96,96,96,${alpha})`;
}
